import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useLanguage } from "@/context/LanguageContext";
import { Mail, Phone, MapPin, ArrowRight } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const initialForm = { name: "", email: "", phone: "", subject: "", message: "" };

export const Contact = () => {
  const { t, lang } = useLanguage();
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error(t.contact.error);
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contacts`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || undefined,
        subject: form.subject.trim() || undefined,
        message: form.message.trim(),
      });
      toast.success(t.contact.success);
      setForm(initialForm);
    } catch (err) {
      console.error(err);
      toast.error(t.contact.error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative bg-[#0A0A0A] border-t border-white/5"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-16">
        <div className="lg:col-span-5">
          <div className="eyebrow mb-6" data-testid="contact-eyebrow">
            {t.contact.eyebrow}
          </div>
          <h2
            data-testid="contact-title"
            className="font-serif-display text-4xl sm:text-5xl md:text-6xl font-light text-[#F8F8F8] leading-[1.05] tracking-tight"
          >
            {t.contact.title}
          </h2>
          <p
            data-testid="contact-body"
            className="mt-8 text-[#A1A1AA] text-base md:text-lg font-light leading-relaxed max-w-md"
          >
            {t.contact.body}
          </p>

          <div className="mt-12 space-y-7">
            <div className="flex items-start gap-4" data-testid="contact-office">
              <MapPin size={18} className="text-[#D4AF37] mt-1 shrink-0" />
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#71717A]">
                  {t.contact.office}
                </div>
                <div className="mt-1 text-[#F8F8F8]">{t.contact.office_addr}</div>
              </div>
            </div>
            <div className="flex items-start gap-4" data-testid="contact-phone">
              <Phone size={18} className="text-[#D4AF37] mt-1 shrink-0" />
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#71717A]">
                  {t.contact.phone_label}
                </div>
                <a
                  href={`tel:${t.contact.phone_value.replace(/\s/g, "")}`}
                  className="mt-1 block text-[#F8F8F8] hover:text-[#D4AF37] transition-colors"
                  dir="ltr"
                >
                  {t.contact.phone_value}
                </a>
              </div>
            </div>
            <div className="flex items-start gap-4" data-testid="contact-email">
              <Mail size={18} className="text-[#D4AF37] mt-1 shrink-0" />
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#71717A]">
                  {t.contact.email_label}
                </div>
                <a
                  href={`mailto:${t.contact.email_value}`}
                  className="mt-1 block text-[#F8F8F8] hover:text-[#D4AF37] transition-colors"
                  dir="ltr"
                >
                  {t.contact.email_value}
                </a>
              </div>
            </div>
          </div>
        </div>

        <form
          data-testid="contact-form"
          onSubmit={onSubmit}
          className="lg:col-span-7 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-8"
        >
          <Field
            label={t.contact.name}
            name="name"
            value={form.name}
            onChange={onChange}
            required
            testid="contact-name"
          />
          <Field
            label={t.contact.email}
            name="email"
            type="email"
            value={form.email}
            onChange={onChange}
            required
            testid="contact-email-input"
          />
          <Field
            label={t.contact.phone}
            name="phone"
            value={form.phone}
            onChange={onChange}
            testid="contact-phone-input"
          />
          <Field
            label={t.contact.subject}
            name="subject"
            value={form.subject}
            onChange={onChange}
            testid="contact-subject"
          />
          <div className="md:col-span-2">
            <label className="block text-[10px] uppercase tracking-[0.25em] text-[#71717A] mb-3">
              {t.contact.message}
            </label>
            <textarea
              data-testid="contact-message"
              name="message"
              value={form.message}
              onChange={onChange}
              rows={5}
              required
              className="w-full bg-transparent border-b border-white/20 focus:border-[#D4AF37] outline-none py-3 px-0 text-[#F8F8F8] placeholder:text-white/30 transition-colors resize-none"
            />
          </div>

          <div className="md:col-span-2 flex items-center justify-between gap-4 pt-2">
            <div className="text-xs text-[#71717A] max-w-sm">
              {t.footer.tag}
            </div>
            <button
              data-testid="contact-submit-button"
              type="submit"
              disabled={submitting}
              className="group inline-flex items-center gap-3 bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#E6C657] disabled:opacity-60 disabled:cursor-not-allowed transition-colors duration-300 px-8 py-4 text-xs uppercase tracking-[0.25em] font-medium"
            >
              <span>{submitting ? t.contact.sending : t.contact.submit}</span>
              <ArrowRight
                size={16}
                className={`${
                  lang === "ar" ? "rotate-180" : ""
                } group-hover:translate-x-1 transition-transform`}
              />
            </button>
          </div>
        </form>
      </div>
    </section>
  );
};

const Field = ({ label, name, type = "text", value, onChange, required, testid }) => (
  <div>
    <label className="block text-[10px] uppercase tracking-[0.25em] text-[#71717A] mb-3">
      {label}
      {required ? <span className="text-[#D4AF37]"> *</span> : null}
    </label>
    <input
      data-testid={testid}
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      required={required}
      className="w-full bg-transparent border-b border-white/20 focus:border-[#D4AF37] outline-none py-3 px-0 text-[#F8F8F8] placeholder:text-white/30 transition-colors"
    />
  </div>
);

export default Contact;
